import type { AgentApiAnalysisInsightsShow } from '@/types/serializers';
import InsightLeadTimeBar from './InsightLeadTimeBar';
import InsightTrendChart from './InsightTrendChart';
import InsightFunnelChart from './InsightFunnelChart';
import InsightConsultantTable from './InsightConsultantTable';
import DeltaIndicator from './DeltaIndicator';
import InsightMetricSelect from './InsightMetricSelect';

interface Props {
  insight: AgentApiAnalysisInsightsShow;
  isGrouped: boolean;
  insightMetric: 'lead_time' | 'sales';
  onMetricChange: (value: 'lead_time' | 'sales') => void;
}

/**
 * インサイト「リードタイム」ビュー。
 * リードタイム概要 / 成約件数・リードタイム推移 / 進捗別ファネル / 担当者別明細を表示する。
 */
export default function InsightLeadTimeView({
  insight,
  isGrouped,
  insightMetric,
  onMetricChange,
}: Props) {
  const { summary, stage_breakdown, trend, funnel, consultant_table } = insight;

  const isSummaryEmpty =
    summary.total_contracts === 0 && summary.pass_rate === 0 && summary.average_lead_time === 0;

  return (
    <div className="mt-md space-y-4">
      <section className="flex flex-col gap-lg rounded-sm bg-white p-md shadow-layer1">
        <div className="flex flex-col gap-sm">
          <InsightMetricSelect value={insightMetric} onChange={onMetricChange}>
            <div className="flex flex-col items-start gap-xs">
              <p className="m-0 text-heading-sm text-main">リードタイム</p>
              <div className="flex items-end gap-xs">
                <span className="text-heading-xl text-main">
                  平均{isSummaryEmpty ? '-' : summary.average_lead_time}日
                </span>
                {!isSummaryEmpty && (
                  <DeltaIndicator
                    delta={summary.average_lead_time_delta}
                    positiveIsGood={false}
                    suffix="日"
                  />
                )}
              </div>
            </div>
          </InsightMetricSelect>

          <div className="flex flex-wrap gap-sm">
            <div className="flex h-[75px] min-w-[160px] items-center rounded-sm border-l-4 border-solid border-primary bg-white px-xl py-sm shadow-layer1">
              <div className="flex flex-col items-start gap-xxs">
                <p className="m-0 text-heading-xxs text-main">成約数</p>
                <div className="flex items-end gap-xs">
                  <span className="text-heading-md text-main">
                    {isSummaryEmpty ? '-' : summary.total_contracts}件
                  </span>
                  {!isSummaryEmpty && (
                    <DeltaIndicator
                      delta={summary.total_contracts_delta}
                      positiveIsGood
                      suffix=""
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="flex h-[75px] min-w-[160px] items-center rounded-sm border-l-4 border-solid border-primary bg-white px-xl py-sm shadow-layer1">
              <div className="flex flex-col items-start gap-xxs">
                <p className="m-0 text-heading-xxs text-main">通過率</p>
                <div className="flex items-end gap-xs">
                  <span className="text-heading-md text-main">
                    {isSummaryEmpty ? '-' : summary.pass_rate}%
                  </span>
                  {!isSummaryEmpty && (
                    <DeltaIndicator delta={summary.pass_rate_delta} positiveIsGood suffix="pt" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <InsightLeadTimeBar groups={stage_breakdown} isGrouped={isGrouped} />
      </section>

      <section className="grid gap-4" style={{ gridTemplateColumns: '2fr 1fr' }}>
        <div className="min-w-0 rounded-md border border-border bg-white p-4">
          <InsightTrendChart trend={trend} isGrouped={isGrouped} />
        </div>
        <div className="min-w-0 rounded-md border border-border bg-white p-4">
          <InsightFunnelChart rows={funnel} />
        </div>
      </section>

      <section>
        <InsightConsultantTable rows={consultant_table} />
      </section>
    </div>
  );
}
